import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataCacheProvider } from "@/context/DataCacheContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { getNavForUser } from "@/lib/permissions";
import PdfViewerPortal from "@/components/PdfViewerPortal";

// Login eagerly loaded (critical, small)
import Login from "@/pages/Login";

// All other pages are lazy-loaded for faster initial bundle + instant subsequent navs
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Stock = lazy(() => import("@/pages/Stock"));
const CuttingHistory = lazy(() => import("@/pages/CuttingHistory"));
const IncomingHistory = lazy(() => import("@/pages/IncomingHistory"));
const Statistics = lazy(() => import("@/pages/Statistics"));
const KartelaRequests = lazy(() => import("@/pages/KartelaRequests"));
const Products = lazy(() => import("@/pages/Products"));
const Customers = lazy(() => import("@/pages/Customers"));
const Users = lazy(() => import("@/pages/Users"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const Backups = lazy(() => import("@/pages/Backups"));
const FactoryOrders = lazy(() => import("@/pages/FactoryOrders"));
const PendingOrders = lazy(() => import("@/pages/PendingOrders"));
const CuttingTasks = lazy(() => import("@/pages/CuttingTasks"));
const Reservations = lazy(() => import("@/pages/Reservations"));
const RetailOrders = lazy(() => import("@/pages/RetailOrders"));
const Returns = lazy(() => import("@/pages/Returns"));
const DefectReport = lazy(() => import("@/pages/DefectReport"));
const MyCustomers = lazy(() => import("@/pages/MyCustomers"));
const Shipping = lazy(() => import("@/pages/Shipping"));
const StoreDelivery = lazy(() => import("@/pages/StoreDelivery"));
const Proforma = lazy(() => import("@/pages/Proforma"));
const Blacklist = lazy(() => import("@/pages/Blacklist"));
const Cargo = lazy(() => import("@/pages/Cargo"));
const Kartelas = lazy(() => import("@/pages/Kartelas"));
const RetailPrices = lazy(() => import("@/pages/RetailPrices"));
const WholesalePrices = lazy(() => import("@/pages/WholesalePrices"));
const SpecialPrices = lazy(() => import("@/pages/SpecialPrices"));
const PurchasePrices = lazy(() => import("@/pages/PurchasePrices"));
const AccountStatements = lazy(() => import("@/pages/AccountStatements"));
const CustomerAddresses = lazy(() => import("@/pages/CustomerAddresses"));
const Factories = lazy(() => import("@/pages/Factories"));
const SalesNotes = lazy(() => import("@/pages/SalesNotes"));
const CustomerStats = lazy(() => import("@/pages/CustomerStats"));

const NAV_TO_PATH = {
  dashboard: "/",
  stock: "/stok",
  cutting_history: "/kesim",
  incoming_history: "/gelen",
  statistics: "/istatistik",
  products: "/urunler",
  customers: "/musteriler",
  factory_orders: "/fabrika-siparisleri",
  pending_orders: "/bekleyen",
  cutting_tasks: "/kesim-gorevleri",
  retail_orders: "/siparisler",
  shipping: "/sevkiyat",
  store_delivery: "/magaza-teslim",
  proforma: "/proforma",
  blacklist: "/kara-liste",
  cargo: "/kargo",
  kartela: "/kartela",
  kartela_requests: "/kartela-talepleri",
  retail_prices: "/perakende-fiyat",
  wholesale_prices: "/toptan-fiyat",
  special_prices: "/ozel-fiyatlar",
  purchase_prices: "/gelis-fiyatlari",
  account_statements: "/muhasebe-ekstreleri",
  factories: "/fabrikalar",
  sales_notes: "/pazarlama-notlari",
  activity: "/gecmis",
  customer_stats: "/musteri-istatistik",
  users: "/kullanicilar",
  backups: "/yedekler",
};

function RoleGuard({ nav, children }) {
  const { user } = useAuth();
  const allowed = getNavForUser(user);
  if (!allowed.includes(nav)) return <Navigate to="/" replace />;
  return children;
}

function HomeRoute() {
  const { user } = useAuth();
  const allowed = getNavForUser(user);
  if (!allowed.includes("dashboard") && allowed.length > 0) {
    const target = NAV_TO_PATH[allowed[0]] || "/stok";
    return <Navigate to={target} replace />;
  }
  return <Dashboard />;
}

function PageFallback() {
  return (
    <div className="p-8 text-muted-foreground text-sm" data-testid="route-loading">
      Yükleniyor...
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DataCacheProvider>
      <RealtimeProvider>
      <NotificationsProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/giris" element={<Login />} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route element={<Suspense fallback={<PageFallback />}><Routed /></Suspense>}>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/stok" element={<RoleGuard nav="stock"><Stock /></RoleGuard>} />
              <Route path="/kesim" element={<RoleGuard nav="cutting_history"><CuttingHistory /></RoleGuard>} />
              <Route path="/gelen" element={<RoleGuard nav="incoming_history"><IncomingHistory /></RoleGuard>} />
              <Route path="/istatistik" element={<RoleGuard nav="statistics"><Statistics /></RoleGuard>} />
              <Route path="/urunler" element={<RoleGuard nav="products"><Products /></RoleGuard>} />
              <Route path="/musteriler" element={<RoleGuard nav="customers"><Customers /></RoleGuard>} />
              <Route path="/fabrika-siparisleri" element={<RoleGuard nav="factory_orders"><FactoryOrders /></RoleGuard>} />
              <Route path="/bekleyen" element={<RoleGuard nav="pending_orders"><PendingOrders /></RoleGuard>} />
              <Route path="/kesim-gorevleri" element={<RoleGuard nav="cutting_tasks"><CuttingTasks /></RoleGuard>} />
              <Route path="/opsiyonlar" element={<RoleGuard nav="reservations"><Reservations /></RoleGuard>} />
              <Route path="/siparisler" element={<RoleGuard nav="retail_orders"><RetailOrders /></RoleGuard>} />
              <Route path="/sevkiyat" element={<RoleGuard nav="shipping"><Shipping /></RoleGuard>} />
              <Route path="/magaza-teslim" element={<RoleGuard nav="store_delivery"><StoreDelivery /></RoleGuard>} />
              <Route path="/proforma" element={<RoleGuard nav="proforma"><Proforma /></RoleGuard>} />
              <Route path="/kara-liste" element={<RoleGuard nav="blacklist"><Blacklist /></RoleGuard>} />
              <Route path="/kargo" element={<RoleGuard nav="cargo"><Cargo /></RoleGuard>} />
              <Route path="/kartela" element={<RoleGuard nav="kartela"><Kartelas /></RoleGuard>} />
              <Route path="/kartela-talepleri" element={<RoleGuard nav="kartela_requests"><KartelaRequests /></RoleGuard>} />
              <Route path="/perakende-fiyat" element={<RoleGuard nav="retail_prices"><RetailPrices /></RoleGuard>} />
              <Route path="/toptan-fiyat" element={<RoleGuard nav="wholesale_prices"><WholesalePrices /></RoleGuard>} />
              <Route path="/ozel-fiyatlar" element={<RoleGuard nav="special_prices"><SpecialPrices /></RoleGuard>} />
              <Route path="/gelis-fiyatlari" element={<RoleGuard nav="purchase_prices"><PurchasePrices /></RoleGuard>} />
              <Route path="/muhasebe-ekstreleri" element={<RoleGuard nav="account_statements"><AccountStatements /></RoleGuard>} />
              <Route path="/musteri-adresleri" element={<RoleGuard nav="customer_addresses"><CustomerAddresses /></RoleGuard>} />
              <Route path="/fabrikalar" element={<RoleGuard nav="factories"><Factories /></RoleGuard>} />
              <Route path="/pazarlama-notlari" element={<RoleGuard nav="sales_notes"><SalesNotes /></RoleGuard>} />
              <Route path="/iadeler" element={<RoleGuard nav="returns"><Returns /></RoleGuard>} />
              <Route path="/defo-raporu" element={<RoleGuard nav="defect_report"><DefectReport /></RoleGuard>} />
              <Route path="/musteri-istatistik" element={<RoleGuard nav="customer_stats"><CustomerStats /></RoleGuard>} />
              <Route path="/musterilerim" element={<RoleGuard nav="my_customers"><MyCustomers /></RoleGuard>} />
              <Route path="/gecmis" element={<RoleGuard nav="activity"><ActivityLog /></RoleGuard>} />
              <Route path="/kullanicilar" element={<RoleGuard nav="users"><Users /></RoleGuard>} />
              <Route path="/yedekler" element={<RoleGuard nav="backups"><Backups /></RoleGuard>} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
      <PdfViewerPortal />
      </NotificationsProvider>
      </RealtimeProvider>
      </DataCacheProvider>
    </AuthProvider>
  );
}

// Outlet renderer for the Suspense-wrapped layer
import { Outlet } from "react-router-dom";
function Routed() {
  return <Outlet />;
}
